import { eventChannel } from 'redux-saga';
import restClient from 'config/httpClient';
import { put, take, fork, call, delay, takeEvery, select } from 'redux-saga/effects';

import { TABLET_WIDTH } from 'constants/Layout';
import { PATH, PATH_NAME } from 'constants/Routes';
import { HAS_NEW_MESSAGE, PREVIEW } from 'constants/Parameters';

import { createNotification } from 'modules/notifications/actions';

import { fetchRefDataRequest } from 'modules/refdata/actions';
import * as actionTypes from './actionTypes';
import { isMobile as isMobileSelector } from './selectors';
import { FAILURE_REQUEST, SUCCESS_REQUEST } from './constants';
import { getBaseActionType, hasAnsweredCookies, isActionRequest, simpleRequest } from './utils';

import {
  connectionLost,
  connectionEstablished,
  openCookieModal,
  openChatModal,
  sendMediaMetricsSuccess,
  sendMediaMetricsFailure,
  setIsMobile,
} from './actions';

export function* startApp() {
  const hasCookies = yield call(hasAnsweredCookies);

  const { pathname } = window.location;
  const isTest = PATH[PATH_NAME.TEST].match(pathname);
  const isCustomize = PATH[PATH_NAME.CUSTOMIZE].match(pathname);

  if (!PREVIEW && !hasCookies && !isCustomize && !isTest) {
    yield put(openCookieModal());
    yield take(actionTypes.CLOSE_COOKIE_MODAL);
  }

  if (HAS_NEW_MESSAGE) {
    yield put(openChatModal());
  }

  if (!isTest) {
    yield put(fetchRefDataRequest());
  }
}

function* connectionWatcher() {
  const channel = eventChannel((emitter) => {
    window.addEventListener('online', emitter);
    window.addEventListener('offline', emitter);
    return () => {
      window.removeEventListener('online', emitter);
      window.removeEventListener('offline', emitter);
    };
  });

  while (true) {
    const { type } = yield take(channel);
    if (type === 'online') {
      yield put(connectionEstablished());
      yield put(
        createNotification({
          type: 'success',
          message: {
            id: 'networks.established',
            defaultMessage: 'Internet connection recovered',
          },
        })
      );
    } else {
      yield put(connectionLost());
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'networks.lost',
            defaultMessage: 'Connection internet lost',
          },
        })
      );
    }
  }
}

function* mobileWatcher() {
  const channel = eventChannel((emitter) => {
    window.addEventListener('resize', emitter);
    return () => {
      window.removeEventListener('resize', emitter);
    };
  });

  while (true) {
    const { type } = yield take(channel);
    if (type === 'resize') {
      const isMobile = yield select(isMobileSelector);
      const isMobileNewSize = window.innerWidth <= TABLET_WIDTH;
      if (isMobile !== isMobileNewSize) {
        yield put(setIsMobile(isMobileNewSize));
      }
    }
  }
}

function* retryMediaMetrics(request) {
  for (let i = 0; i < 5; i++) {
    try {
      return yield call(restClient.request, request);
    } catch {
      if (i < 4) {
        yield delay(2000);
      }
    }
  }
  // attempts failed after 5 attempts
  throw new Error('API request failed');
}

function* sendMediaMetrics({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(retryMediaMetrics, request);
    yield put(sendMediaMetricsSuccess(data));
  } catch (error) {
    yield put(sendMediaMetricsFailure(error));
  }
}

/**
 * @param {import('types/generic.types').Action<unknown>} action
 */
export function* putAndWait(action) {
  if (isActionRequest(action)) {
    const baseAction = getBaseActionType(action.type);
    yield put(action);
    return yield take([`${baseAction}${SUCCESS_REQUEST}`, `${baseAction}${FAILURE_REQUEST}`]);
  }
  throw new Error('Invalid error - The given params is not a request action');
}

/** @param {import('types/generic.types').Action<any>} action */
function* asyncActionWatcher({ type, meta: metaRequest }) {
  const requestId = metaRequest['@async-id'];
  const typeBase = getBaseActionType(type);

  const takeFn = ({ type: responseType, meta = {} }) =>
    (responseType === `${typeBase}SUCCESS` || responseType === `${typeBase}FAILURE`) &&
    meta['@async-id'] === requestId &&
    meta['@async-root'] === type;

  const { type: responseType, payload } = yield take(takeFn);
  const callback = responseType === `${typeBase}SUCCESS` ? metaRequest.resolve : metaRequest.reject;
  yield call(callback, payload);
}

const isAsyncFn = ({ type, meta }) => type.endsWith('REQUEST') && meta?.['@async'];

const isAutoRequest = ({ type, meta }) => type.endsWith('REQUEST') && meta?.['@app-request'];

export default function* appSagas() {
  yield takeEvery(actionTypes.SEND_MEDIA_METRICS, sendMediaMetrics);
  yield fork(connectionWatcher);
  yield fork(mobileWatcher);
  yield fork(startApp);

  yield takeEvery(isAsyncFn, asyncActionWatcher);
  yield takeEvery(isAutoRequest, simpleRequest);
}
